import { useEffect, useRef, useState } from 'react';
import { Icons } from '../Icons.jsx';
import { speak } from '../../utils/speech.js';
import { answersMatch } from '../../utils/normalize.js';
import { BALL } from '../../theme.js';
import { t } from '../../i18n.js';

const T = BALL;

export default function Translation({ exercise, lang, onResult, locked, registerSubmit }) {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setValue('');
    inputRef.current?.focus();
  }, [exercise]);

  const isToHR = exercise.direction === 'toHR';
  const eyebrowColor = isToHR ? T.primary : T.gold;
  const eyebrow = isToHR
    ? (lang === 'en' ? 'TRANSLATE TO CROATIAN' : 'ÖVERSÄTT TILL KROATISKA')
    : (lang === 'en' ? 'TRANSLATE FROM CROATIAN' : 'ÖVERSÄTT FRÅN KROATISKA');
  const titleText = isToHR
    ? (lang === 'en' ? 'Say this in Croatian' : 'Säg detta på kroatiska')
    : (lang === 'en' ? 'What does this mean?' : 'Vad betyder detta?');

  function submit() {
    if (locked) return;
    const correct = answersMatch(value, exercise.accept);
    onResult({
      correct,
      given: value,
      expected: Array.isArray(exercise.accept) ? exercise.accept[0] : exercise.accept,
    });
  }

  registerSubmit?.(submit);

  return (
    <div style={{ padding: '4px 22px 0', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={{
          fontSize: 11, fontWeight: 800, color: eyebrowColor, letterSpacing: '0.12em',
        }}>{eyebrow}</div>
        <h2 style={{
          fontSize: 24, fontWeight: 900, color: T.ink, margin: '6px 0 0', lineHeight: 1.15,
        }}>{titleText}</h2>
      </div>

      {isToHR ? (
        <div style={{
          background: T.bgAlt, border: `2px solid ${T.hairline}`,
          borderRadius: 16, padding: '18px 16px',
          boxShadow: `0 3px 0 ${T.hairline}`,
          display: 'flex', alignItems: 'center', gap: 14, minHeight: 80,
        }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.ink }}>
            {exercise.prompt}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => speak(exercise.speakText)}
          style={{
            appearance: 'none', cursor: 'pointer',
            background: T.bgAlt, border: `2px solid ${T.hairline}`,
            borderRadius: 16, padding: '18px 16px',
            boxShadow: `0 3px 0 ${T.hairline}`,
            display: 'flex', alignItems: 'center', gap: 14, minHeight: 80,
            textAlign: 'left', fontFamily: 'inherit', width: '100%',
          }}
        >
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: T.blue, color: '#fff', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 0 ${T.blueDeep}`,
          }}>
            {Icons.speaker('#fff')}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.primary }}>
              {exercise.prompt}
            </div>
            <div style={{
              fontSize: 11, fontWeight: 800, color: T.primary,
              letterSpacing: '0.06em', marginTop: 4, opacity: 0.85,
            }}>
              ▶ {lang === 'en' ? 'TAP TO HEAR' : 'TRYCK FÖR ATT HÖRA'}
            </div>
          </div>
        </button>
      )}

      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={locked}
        rows={2}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder={isToHR
          ? (lang === 'en' ? 'Type in Croatian…' : 'Skriv på kroatiska…')
          : (lang === 'en' ? 'Type the translation…' : 'Skriv översättningen…')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        style={{
          width: '100%', resize: 'none',
          border: `2px solid ${T.hairline}`,
          borderRadius: 14, padding: '14px 16px',
          fontSize: 18, fontFamily: 'inherit', fontWeight: 600,
          color: T.ink, background: T.bgAlt, outline: 'none',
        }}
      />
    </div>
  );
}
